import React, {
  isValidElement,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useMediaQuery } from '@mui/material';
import SocketNotifications from 'components/socket-notifications/SocketNotifications';
import SmallSearch from 'components/small-search/SmallSearch';
import { usePermission, useQueryStringFilters, useScrollPosition } from 'hooks';
import {
  scrollTop,
  isMobile,
  getEnv,
  getLargeLogoURL,
  isAppLang,
  isResourceProfilePath,
  isResourcesListPath,
} from 'utils';
import { UiIcon } from 'ui-components';
import VersionList from '../../version-list/VersionList';
import UserProfile from './user-profile/UserProfile';
import HeaderFilter from './header-filter/HeaderFilter';
import QueryFilter from './header-filter/query-filter/QueryFilter';
import SupportMenu from '../../support-menu/SupportMenu';
import { StyledLayoutHeader } from './styles';

const PageTitle = () => {
  const titleIcon = useSelector((state) => state.pageReducer.titleIcon);
  const titleIconColor = useSelector(
    (state) => state.pageReducer.titleIconColor
  );
  const titleIconSrc = useSelector((state) => state.pageReducer.titleIconSrc);
  const pageTitle = useSelector((state) => state.pageReducer.pageTitle);

  return (
    <>
      <Helmet>
        <title>
          {getEnv('REACT_APP_TITLE')} | {pageTitle}
        </title>
      </Helmet>
      {titleIcon && (
        <UiIcon
          icon={titleIcon}
          color={titleIconColor}
          hover={titleIconColor}
          size={25}
          className='ml-14'
        />
      )}
      {titleIconSrc && (
        <UiIcon src={titleIconSrc} width={25} height={25} className='ml-14' />
      )}
      {/* عنوان صفحه */}
      <span className='title'>{pageTitle}</span>
    </>
  );
};

const COLLAPSE_THRESHOLD = 100;

/**
 * The header sits above the scroll container in a flex column, so removing
 * the search row makes the container taller and the browser clamps scrollTop
 * back under the threshold. On short pages that re-expands the header and
 * the two states flicker on every wheel tick. Only collapse when the page
 * will still be scrolled past the threshold after the row is gone.
 */
const canStayCollapsed = (headerEl, headerTopEl) => {
  const container = document.getElementById('page-container');
  if (!container || !headerEl || !headerTopEl) return false;

  /**
   * After collapse only the top bar and the header's own padding remain, so
   * the padding must be excluded from the height that disappears.
   */
  const { paddingTop, paddingBottom } = getComputedStyle(headerEl);
  const removedHeight =
    headerEl.offsetHeight -
    headerTopEl.offsetHeight -
    parseFloat(paddingTop) -
    parseFloat(paddingBottom);
  const maxScrollAfterCollapse =
    container.scrollHeight - container.clientHeight - removedHeight;

  return maxScrollAfterCollapse > COLLAPSE_THRESHOLD;
};

const LayoutHeader = ({ action, className }) => {
  const { userPermission } = usePermission();
  const { scrollPosition } = useScrollPosition();
  const headerRef = useRef(null);
  const headerTopRef = useRef(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const smallMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const logo = useSelector((state) => state.pageReducer.logo);
  const displayDateTimePicker = useSelector(
    (state) => state.pageReducer.displayDateTimePicker
  );
  const displayDatePicker = useSelector(
    (state) => state.pageReducer.displayDatePicker
  );
  const displayQuery = useSelector((state) => state.pageReducer.displayQuery);
  const isLoggedIn = useSelector((state) => state.authReducer.isLoggedIn);
  const navigate = useNavigate();
  const location = useLocation();
  const isArabicLanguage = isAppLang('ar');
  const isContentsPage = location.pathname.includes('contents');
  const isStatisticsPage = location.pathname.includes('statistics');
  const isResourceProfilePage = isResourceProfilePath(location.pathname);
  const isResourcesListPage = isResourcesListPath(location.pathname);

  useQueryStringFilters();

  // پرمیشن به لیست اعلان‌ها
  const { permission: permi_notification_index } = useMemo(
    () => userPermission('Notification', 'index'),
    [userPermission]
  );

  const { permission: hasFreeSearchAccess } = useMemo(
    () => userPermission('FrontEnd', 'freeSearch'),
    [userPermission]
  );

  const { permission: hasMegaSubjectPermission } = useMemo(
    () => userPermission('FrontEnd', 'megaSubject'),
    [userPermission]
  );

  const canUseFreeSearch = hasFreeSearchAccess && !isArabicLanguage;
  const hasMegaSubjectAccess = canUseFreeSearch && hasMegaSubjectPermission;
  /**
   * The resources list searches resources by free text, unrelated to subjects,
   * so it gets the search row without the subject filter or displayQuery.
   */
  const showHeaderSearchRow =
    ((isContentsPage || isStatisticsPage || isResourceProfilePage) &&
      displayQuery &&
      (canUseFreeSearch || hasMegaSubjectAccess)) ||
    (isResourcesListPage && canUseFreeSearch);
  const searchRowHasFilters = !isResourcesListPage;

  const navigateToHome = () => {
    if (isLoggedIn) return navigate('/');
  };

  /**
   * On scroll down (any viewport), the search row collapses and its query
   * filter moves back up into the top bar; scrolling back to the top
   * restores the search row. This mirrors the same behavior across mobile
   * and desktop.
   */
  useEffect(() => {
    if (scrollPosition <= COLLAPSE_THRESHOLD) {
      setIsCollapsed(false);
      return;
    }
    if (isCollapsed) return;

    const hasRoom =
      !showHeaderSearchRow ||
      canStayCollapsed(headerRef.current, headerTopRef.current);
    if (hasRoom) setIsCollapsed(true);
  }, [scrollPosition, isCollapsed, showHeaderSearchRow]);

  const showSearchRow = showHeaderSearchRow && !isCollapsed;
  /**
   * On mobile, the header filters move into the search row so that, per the
   * design, the subject and calendar sit side by side with equal width in
   * their own row.
   */
  const filtersInSearchRow =
    showSearchRow && smallMobile && searchRowHasFilters;
  const showQueryFilterInTop = !showHeaderSearchRow || isCollapsed;

  return (
    <StyledLayoutHeader
      ref={headerRef}
      className={classNames(
        'layout-header',
        className,
        isCollapsed ? 'collapsed' : 'expand',
        { 'has-search-row': showHeaderSearchRow }
      )}
      style={
        // این فاصله فقط برای جاباز کردن زیر header-filterِ absolute است
        (displayDateTimePicker || displayDatePicker || displayQuery) &&
        smallMobile &&
        !isCollapsed &&
        !filtersInSearchRow
          ? { marginBottom: '48px' }
          : {}
      }
    >
      <div className='header-top' ref={headerTopRef}>
        {logo && (
          <img
            className='app-logo'
            onClick={navigateToHome}
            src={getLargeLogoURL()}
            alt=''
          />
        )}
        <div
          className={classNames('right-section flex', {
            isMobile: isMobile(),
          })}
          onClick={() => isMobile() && scrollTop()}
        >
          <PageTitle />
          {isLoggedIn && <VersionList />}
        </div>
        <div className='left-section flex'>
          {/* فیلتر های هدر */}
          {!filtersInSearchRow && (
            <HeaderFilter showQueryFilter={showQueryFilterInTop} />
          )}
          <div className='header-actions flex'>
            {isLoggedIn && (
              <>
                {isValidElement(action) && action}
                <span className='seperator' />
                <SupportMenu />
                {/* اعلان‌ها*/}
                {permi_notification_index && (
                  <SocketNotifications
                    className='socket-menu'
                    icon='ri-notification-2-line'
                    data-testid='header-notification-alarm'
                  />
                )}
                {/* پروفایل کاربر */}
                <UserProfile />
              </>
            )}
          </div>
        </div>
      </div>

      {showSearchRow && (
        <>
          <div className='header-divider' />
          <div className='header-search-row'>
            {searchRowHasFilters &&
              (filtersInSearchRow ? <HeaderFilter /> : <QueryFilter />)}
            <SmallSearch variant='header' />
          </div>
        </>
      )}
    </StyledLayoutHeader>
  );
};

LayoutHeader.propTypes = {
  action: PropTypes.element,
  className: PropTypes.string,
};

export default memo(LayoutHeader);
